// @flow

import React from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import mediaApi from '../../network/api/mediaApi';
import type { Media } from '../../types/keepeekType';
import Loader from './Loader';
import KeepeekMediaPanoramic from '../media/KeepeekMediaPanoramic';
import KeepeekMedia360 from '../media/KeepeekMedia360';
import { openModalWithElements } from '../../state/page/pageService';
import { isMobile } from '../../const';
import logo from '../../img/logo.svg';

type Props = {
  keepeekId?: string,
  keepeekMedia?: Media,
  className?: string,
  disableClick?: boolean,
  enablePatchwork?: boolean,
  fullscreen?: boolean,
  openModalWithElements: Function,
  is360Callback?: Function,
  closePdfCallBack?: Function,
  showTitle?: boolean,
  href?: string,
  children?: React.Node,
}

type State = {
  media: ?Media,
  isLoading: boolean,
}

export function KeepeekPictureNoMedia() {
  return (
    <img
      className="keepeek-picture keepeek-picture--no-media"
      alt={I18n.t('keepeekPicture.noMedia')}
      src={logo}
    />
  );
}

class KeepeekMedia extends React.Component<Props, State> {
  static defaultProps = {
    className: '',
    keepeekId: null,
    keepeekMedia: null,
    disableClick: false,
    enablePatchwork: false,
    is360: false,
    fullscreen: false,
    is360Callback: null,
    closePdfCallBack: null,
    showTitle: true,
  };

  state: State = {
    media: null,
    isLoading: true,
  };

  componentDidMount() {
    if (this.props.keepeekMedia && this.props.is360Callback) {
      this.props.is360Callback(this.props.keepeekMedia.virtualVisitFullScreen
        || (this.props.keepeekMedia.medias && this.props.keepeekMedia.medias.length > 0));
    }
    this.updateMedia(this.props.keepeekId);
  }

  updateMedia = (id: string) => {
    this.setState({ media: null });
    if (id == null) {
      this.setState({ isLoading: false });
      return;
    }

    if (this.props.fullscreen) {
      this.setState({ isLoading: true });
    }
    mediaApi.fetchMedia(id)
      .then(resp => resp.json())
      .then((json) => {
        if (this.props.is360Callback) {
          this.props.is360Callback(json.virtualVisitFullScreen
            || (json.medias && json.medias.length > 0));
        }
        this.setState({ media: json });
      })
      .catch(console.error)
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (nextProps.keepeekId !== this.props.keepeekId) {
      this.updateMedia(nextProps.keepeekId);
      return false;
    }
    return true;
  }

  renderImage = () => {
    const media = this.state.media || this.props.keepeekMedia;
    if (media) {
      const baseThumbnail = (
        <div className="hover-show-title-media">
          <img
            className={`keepeek-picture ${this.props.className || ''}`}
            alt={media.title}
            src={this.props.fullscreen ? media.fullscreen : media.thumbnail}
          />
          <div className="keepeek-media-description">
            <span className="keepeek-media-title">{media.title}</span>
            {this.props.children}
          </div>
        </div>
      );
      if (this.props.disableClick && !this.props.enablePatchwork) {
        if (media.mime_type === 'video') {
          return (
            <video
              className={`keepeek-image-container keepeek-picture ${this.props.className || ''}`}
              controls
              poster={media.thumbnail}
              src={media.originalUrl}
              autoPlay
            >
              <source src={media.originalUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          );
        } else if (media.medias && media.medias.length > 0 && !this.props.enablePatchwork) {
          return (
            <KeepeekMediaPanoramic medias={media.medias} />
          );
        } else if (media.virtualVisitFullScreen && !this.props.enablePatchwork) {
          return (
            <KeepeekMedia360
              mediaUrl={isMobile ? media.virtualVisitFullScreen : media.originalUrl}
            />
          );
        }
        return (
          <>
            <img
              className={`${this.props.fullscreen ? 'fullscreen-image' : 'keepeek-picture'}  ${this.props.className || ''}`}
              alt={media.title}
              src={media.fullscreen}
            />
            <div className="keepeek-media-description">
              {this.props.showTitle && <span className="keepeek-media-title">{media.title}</span>}
              {this.props.children}
            </div>
          </>
        );
      } else if (media.mime_type === 'application/pdf') {
        if (this.props.closePdfCallBack) {
          this.props.closePdfCallBack();
        }
        return (
          <button className="btn-media-content">
            <a
              className="media-content"
              href={this.props.href ? this.props.href : media.originalUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              {baseThumbnail}
            </a>
          </button>
        );
      }
      return (
        <div
          className="keepeek-image-container"
          role="presentation"
          onClick={() =>
            this.props.openModalWithElements({
              medias: [].concat(media),
              itemSelected: 0,
              isOpen: true,
              isAlreadyLoaded: true,
            })}
        >
          {
            media.mime_type === 'video' ?
              <div className="video-preview-container">
                {baseThumbnail}
                <div className="icon-container">
                  <i className="fas fa-play icon" />
                </div>
              </div> :
              baseThumbnail
          }
        </div>
      );
    }
    return (<KeepeekPictureNoMedia />);
  };

  render() {
    return (
      this.state.isLoading ? (
        <div className="keepeek-image-container">
          <Loader isLoading />
        </div>
      ) : (this.renderImage())
    );
  }
}

export default connect(null, { openModalWithElements })(KeepeekMedia);
